import { assert } from '@ember/debug';
import { variation } from 'ember-launch-darkly';
import { DateTime } from 'luxon';

import {
  BILLING_LOW_BALANCE,
  BILLING_LOW_BALANCE_UPDATED,
} from '../constants/status.ts';

//Constants
const flex2ReleaseDate = DateTime.utc(2025, 3, 8);

export default class BillingCredits {
  #credits: number;
  #createdAt: DateTime | null;

  /**
   * @param credits - The credit amount (string or number, required)
   * @param createdAt - The creation date (Date, optional)
   */
  constructor(credits: string | number = '0', createdAt?: Date | null) {
    // Normalize empty string, null, or undefined to '0'.
    // For Flex and Entitlement accounts, credits will be returned
    // as an empty string. We want to ensure BillingCredits constructor
    // doesn't fail in this case, so we default to 0.
    const normalizedCredits = credits === '' || credits == null ? '0' : credits;
    this.#credits = parseFloat(normalizedCredits as string);
    assert(
      `value: ${this.#credits} must be valid number`,
      typeof this.#credits === 'number' && !Number.isNaN(this.#credits),
    );
    if (createdAt) {
      this.#createdAt = DateTime.fromJSDate(createdAt, { zone: 'utc' });
      assert(
        `value: ${this.#createdAt} must be valid date`,
        this.#createdAt.toJSDate() instanceof Date,
      );
    } else {
      this.#createdAt = null;
    }
  }

  /** Returns true if the balance is considered low */
  get isLow() {
    // If the Flex 2 feature flag is turned on and the billing account was created on/after the Flex 2 release date,
    // the low balance threshold is $100
    if (
      variation('hcp-ui-billing-flex-2') &&
      this.#createdAt &&
      this.#createdAt >= flex2ReleaseDate
    ) {
      return this.#credits <= BILLING_LOW_BALANCE_UPDATED && !this.isZero;
    } else {
      // otherwise, the low balance threshold is $10
      return this.#credits <= BILLING_LOW_BALANCE && !this.isZero;
    }
  }

  /** Returns true if the balance is zero */
  get isZero() {
    return this.#credits === 0;
  }

  valueOf() {
    return this.#credits;
  }
}
